/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * ⚠️ DEPRECATED: Legacy Interactive Story UI — DO NOT EDIT ⚠️
 * 
 * This screen is part of the deprecated interactive story journey.
 * It is NOT routed in production and kept only for backwards compatibility.
 * 
 * The LIVE production UI is the Hub System Assessment at /hub
 * 
 * DO NOT make changes to this file unless fixing security vulnerabilities.
 * All new features should be added to the Hub UI instead.
 */

/**
 * Screen 3: What Changes
 * Explains efficiency, smoothness of operation, hot water delivery, and long-term reliability changes
 * Includes links to technical appendices (Layer 3)
 */

import DeepDive from '../../components/DeepDive';
import { deepDives } from '../../content/deepDiveContent';
import { useStoryContext } from '../../context/StoryContext';
import { useRecommendation } from '../../context/RecommendationContext';
import { formatNumber, gbp, kg } from '../../utils/formatters';
import { getOptionDisplayTitle } from '../../utils/displayNames';

function Screen3WhatChanges() {
  const { openTechnicalAppendix } = useStoryContext();
  const { recommendationResult, collectedData } = useRecommendation();
  const primaryOption = recommendationResult?.output?.primary;
  const delta = primaryOption?.deltaVsBaseline;
  const year1 = recommendationResult?.output?.year1;
  const primaryImpact = year1?.options?.find((option) => option.optionId === primaryOption?.id)?.impact;
  const selectedEducation = collectedData?.storyEducationSelections ?? [];
  const showEducation = (id: string) =>
    selectedEducation.length === 0 || selectedEducation.includes(id);

  return (
    <div className="interactive-story-screen">
      <h1 className="screen-title">What Changes</h1>

      <div className="screen-body">
        <p className="screen-text">
          When you upgrade your heating system, four things improve:
        </p>

        <div className="change-list">
          <div className="change-item">
            <div className="change-icon">⚡</div>
            <div className="change-content">
              <h3 className="change-title">Efficiency</h3>
              <div className="change-description">
                Lower running costs and reduced energy waste
                {showEducation('clean-systems') && (
                  <button
                    className="technical-link"
                    onClick={() => openTechnicalAppendix('clean-systems')}
                  >
                    Optional: How does water quality affect efficiency?
                  </button>
                )}
                {showEducation('power-flush-filters') && (
                  <button
                    className="technical-link"
                    onClick={() => openTechnicalAppendix('power-flush-filters')}
                  >
                    Optional: Power flushing & laminar flow filters
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="change-item">
            <div className="change-icon">📊</div>
            <div className="change-content">
              <h3 className="change-title">Smoothness of Operation</h3>
              <div className="change-description">
                More consistent temperatures and better modulation
                {showEducation('modulation-cycling') && (
                  <button
                    className="technical-link"
                    onClick={() => openTechnicalAppendix('modulation-cycling')}
                  >
                    Optional: What&apos;s the difference between modulation and cycling?
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="change-item">
            <div className="change-icon">🚿</div>
            <div className="change-content">
              <h3 className="change-title">Hot Water Delivery</h3>
              <div className="change-description">
                Better flow rates and more reliable supply
                <DeepDive
                  trigger="How do systems differ?"
                  content={deepDives.hotWaterBehavior}
                />
              </div>
            </div>
          </div>

          <div className="change-item">
            <div className="change-icon">🛡️</div>
            <div className="change-content">
              <h3 className="change-title">Long-term Reliability</h3>
              <div className="change-description">
                Fewer breakdowns and longer lifespan
                <DeepDive
                  trigger="Why?"
                  content={deepDives.systemLifespan}
                />
                {showEducation('stress-longevity') && (
                  <button
                    className="technical-link"
                    onClick={() => openTechnicalAppendix('stress-longevity')}
                  >
                    Optional: Understand stress and longevity
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="report-section">
          <h2 className="report-section-title">What the report estimates for you</h2>
          <div className="report-section-grid">
            <div className="report-section-card">
              <p className="report-section-label">Recommended system</p>
              <p className="report-section-value">
                {primaryOption ? getOptionDisplayTitle(primaryOption) : 'Recommendation pending'}
              </p>
            </div>
            <div className="report-section-card">
              <p className="report-section-label">Total savings vs. current system</p>
              <p className="report-section-value">
                {delta?.totalSaving_gbp != null ? gbp(delta.totalSaving_gbp) : 'Not enough info yet'}
              </p>
            </div>
            <div className="report-section-card">
              <p className="report-section-label">Year 1 running cost</p>
              <p className="report-section-value">
                {primaryImpact ? gbp(primaryImpact.costGbp) : 'Not enough info yet'}
              </p>
            </div>
            <div className="report-section-card">
              <p className="report-section-label">Year 1 carbon impact</p>
              <p className="report-section-value">
                {primaryImpact ? kg(primaryImpact.carbonKg) : 'Not enough info yet'}
              </p>
            </div>
            <div className="report-section-card">
              <p className="report-section-label">Year 1 energy use</p>
              <p className="report-section-value">
                {primaryImpact ? `${formatNumber(primaryImpact.totalHeatKwh)} kWh` : 'Not enough info yet'}
              </p>
            </div>
          </div>
        </div>

        {/* Technical appendices section */}
        <div className="appendix-links-section">
          <p className="appendix-links-intro">Want to understand the technical details?</p>
          <div className="appendix-links">
            {showEducation('pipework-flow') && (
              <button
                className="appendix-link-button"
                onClick={() => openTechnicalAppendix('pipework-flow')}
              >
                🔧 Optional: Pipework & Flow
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Screen3WhatChanges;
